<template>
  <b-container class="section-p70">
    <b-row class="mb-3">
      <b-col cols="auto">
        <h2 class="title-blue">{{ t("STATISTICS") }}</h2>
      </b-col>
    </b-row>

    <b-row>
      <b-col cols="12">
        <table class="table b-table table-striped table-borderless stats-table">
          <tbody>
          <tr v-for="stats of statsList" :key="stats.name">
            <th>{{ t(stats.title) }}</th>
            <td>
              <b-button @click="openXlsxDownloadModal(stats.name, t(stats.title))">
                <b-icon-download class="mr-2"/>
                <span>XLSX</span>
              </b-button>
            </td>
          </tr>
          </tbody>
        </table>
      </b-col>
    </b-row>

    <b-modal ref="modalRef"
             size="lg"
             @ok="downloadStatsAsXlsx"
             :ok-disabled="!selectedDate || !selectedTime || loading"
             :title="modalTitle"
             :ok-title="t('DOWNLOAD')"
             :cancel-title="t('CANCEL')"
    >
      <div class="mb-3" v-if="loading">
        <loader active/>
      </div>

      <div class="mb-3">
        <b-form-datepicker
            size="sm"
            v-model="selectedDate"
            :start-weekday="1"
            :date-format-options="{ year: 'numeric', month: 'short', day: '2-digit' }"
            :hide-header="true"
            :locale="locale"
            value-as-date
            required
        />
      </div>

      <div class="mb-3">
        <b-form-timepicker
            size="sm"
            v-model="selectedTime"
            :hide-header="true"
            :locale="locale"
            :hour12="false"
            no-close-button
            required
        />
      </div>
    </b-modal>
  </b-container>
</template>

<script setup lang="ts">
import {useI18n} from "vue-i18n";
import {inject, ref} from "vue";
import moment from "moment";
import {fileNameWithDateTime} from "@/util/fileUtil";
import Loader from "@/components/Loader.vue";
import type {BvEvent} from "bootstrap-vue";
import type {Axios} from "axios";

const {t, locale} = useI18n();
const axios = inject("axios") as Axios;

const modalRef = ref(null);
const statsFileName = ref("");
const modalTitle = ref("");
const selectedDate = ref(new Date());
const selectedTime = ref("00:00:00");
const loading = ref(false);

const statsList = [
  {name: "past-group-participants", title: "PAST_GROUP_PARTICIPANTS"},
  {name: "past-group-successful-participants", title: "PAST_GROUP_PARTICIPANTS_SUCCESSFUL"},
  {name: "running-group-participants", title: "RUNNING_GROUP_PARTICIPANTS"},
  {name: "unstarted-group-participants", title: "UNSTARTED_GROUP_PARTICIPANTS"},
  {name: "unstarted-public-groups", title: "UNSTARTED_PUBLIC_GROUPS"},
];

const openXlsxDownloadModal = (fileName: string, title: string) => {
  selectedDate.value = new Date();
  selectedTime.value = "00:00:00";
  statsFileName.value = fileName;
  modalTitle.value = title;
  modalRef.value.show();
};

const downloadStatsAsXlsx = async (ev: BvEvent) => {
  ev.preventDefault();

  if (!statsFileName.value || !selectedDate.value || !selectedTime.value) {
    return;
  }

  const dateValue = moment(selectedDate.value);
  const timeValue = moment(selectedTime.value, "HH:mm:ss");

  dateValue.hours(timeValue.hours());
  dateValue.minutes(timeValue.minutes());
  dateValue.seconds(timeValue.seconds());
  dateValue.milliseconds(0);

  const params = {
    dateTime: dateValue.toISOString(true),
  };

  loading.value = true;

  try {
    const resp = await axios.get(`/stats/${statsFileName.value}/excel`, {responseType: "blob", params: params});
    const blob = new Blob([resp.data]);
    const link = document.createElement("a");

    link.href = URL.createObjectURL(blob);
    link.download = fileNameWithDateTime(statsFileName.value, dateValue, "xlsx");
    link.click();

    URL.revokeObjectURL(link.href);
  } finally {
    loading.value = false;
    modalRef.value.hide();
  }
};
</script>

<style scoped lang="scss">
.stats-table {
  tr {
    & > * {
      vertical-align: middle;
    }

    & > td {
      text-align: right;
    }
  }
}
</style>
